import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Heart, Wallet, Trophy, Info } from 'lucide-react';
import { formatBRLFromDigits, numberToBRLInputFormat, parseBRLStringToNumber } from '@/utils/brlFormat';
import type { ContractConfiguration, InitialFee } from '@/types/ContractConfiguration';

type PaymentMethod = InitialFee['initialFeePaymentMethod'];

interface SimpleFeeConfigProps {
  /** Valor da causa informado na aba Identificação (base sugerida para o % de êxito) */
  caseValue: number;
  contractConfiguration: ContractConfiguration | null | undefined;
  onContractConfigurationChange: (config: ContractConfiguration | null) => void;
  isProBono: boolean;
  onProBonoChange: (value: boolean) => void;
}

interface FeeState {
  hasInitial: boolean;
  initialAmount: number;
  installments: number;
  firstDate: string; // AAAA-MM-DD
  paymentMethod: PaymentMethod;
  firstPaid: boolean;
  hasSuccess: boolean;
  successType: 'percentage' | 'salaries' | 'benefit';
  benefitFirstDate: string; // AAAA-MM-DD
  percentage: number;
  baseValue: number;
  salaryCount: number;
  salaryValue: number;
}

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'pix', label: 'Pix' },
  { value: 'bank_transfer', label: 'Transferência' },
  { value: 'credit_card', label: 'Cartão de crédito' },
  { value: 'debit_card', label: 'Cartão de débito' },
  { value: 'money', label: 'Dinheiro' },
  { value: 'boleto', label: 'Boleto' },
];

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const toDateInput = (value: unknown): string => {
  if (!value) return '';
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

// Converte a configuração salva (inclusive modelos antigos) para o formulário simples
const stateFromConfig = (cc: ContractConfiguration | null | undefined, caseValue: number): FeeState => {
  const anyCc = (cc || {}) as any;
  const isSuccess = cc?.mode === 'success_fee' || cc?.mode === 'percentage_case';
  return {
    hasInitial: !!cc?.hasInitialFee && (cc?.initialFeeAmount ?? 0) > 0,
    initialAmount: cc?.initialFeeAmount ?? 0,
    installments: Math.max(1, cc?.initialFeeInstallments || 1),
    firstDate: toDateInput(cc?.initialFeeFirstPaymentDate),
    paymentMethod: cc?.initialFeePaymentMethod || 'pix',
    firstPaid: !!cc?.initialFeeAlreadyPaid,
    hasSuccess: isSuccess,
    successType: anyCc.successType === 'salaries' || anyCc.successType === 'benefit' ? anyCc.successType : 'percentage',
    benefitFirstDate: toDateInput(anyCc.benefitFirstPaymentDate),
    percentage: cc?.mode === 'percentage_case' ? anyCc.percentage ?? 30 : anyCc.successPercentage || anyCc.benefitPercentage || 30,
    baseValue: anyCc.successBaseValue ?? (cc?.mode === 'percentage_case' ? anyCc.caseValue : undefined) ?? caseValue ?? 0,
    salaryCount: anyCc.salaryCount ?? 2,
    salaryValue: anyCc.salaryValue ?? 0,
  };
};

const configFromState = (s: FeeState): ContractConfiguration => {
  const installments = Math.max(1, s.installments);
  const initial: InitialFee = s.hasInitial && s.initialAmount > 0
    ? {
        hasInitialFee: true,
        initialFeeAmount: s.initialAmount,
        initialFeeInstallments: installments,
        initialFeeInstallmentValue: s.initialAmount / installments,
        initialFeePaymentMethod: s.paymentMethod,
        initialFeeFirstPaymentDate: s.firstDate ? new Date(`${s.firstDate}T12:00:00`) : undefined,
        initialFeeAlreadyPaid: s.firstPaid,
      }
    : {
        hasInitialFee: false,
        initialFeeAmount: 0,
        initialFeeInstallments: 0,
        initialFeeInstallmentValue: 0,
        initialFeePaymentMethod: 'pix',
        initialFeeAlreadyPaid: false,
      };

  if (s.hasSuccess) {
    const estimated = s.successType === 'salaries'
      ? s.salaryCount * s.salaryValue
      : s.successType === 'benefit'
        ? (s.percentage / 100) * s.salaryCount * s.salaryValue
        : (s.percentage / 100) * s.baseValue;
    return {
      ...initial,
      mode: 'success_fee',
      successType: s.successType,
      successPercentage: s.successType === 'percentage' ? s.percentage : 0,
      successBaseValue: s.successType === 'percentage' ? s.baseValue : undefined,
      salaryCount: s.successType !== 'percentage' ? s.salaryCount : undefined,
      salaryValue: s.successType !== 'percentage' ? s.salaryValue : undefined,
      benefitPercentage: s.successType === 'benefit' ? s.percentage : undefined,
      benefitFirstPaymentDate: s.successType === 'benefit' && s.benefitFirstDate ? new Date(`${s.benefitFirstDate}T12:00:00`) : undefined,
      hasEstimate: estimated > 0,
      estimatedValue: estimated,
    };
  }

  // Apenas honorários iniciais (ou nada): o contrato principal fica zerado
  return { ...initial, mode: 'global_fixed', globalValue: 0, isInstallments: false, installmentCount: 1, globalAlreadyPaid: false };
};

const CurrencyInput = ({ id, value, onChange, placeholder }: { id: string; value: number; onChange: (v: number) => void; placeholder?: string }) => (
  <div className="relative">
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
    <Input
      id={id}
      inputMode="numeric"
      className="pl-9"
      placeholder={placeholder || '0,00'}
      value={value ? numberToBRLInputFormat(value) : ''}
      onChange={(e) => onChange(parseBRLStringToNumber(formatBRLFromDigits(e.target.value)))}
    />
  </div>
);

const Section = ({
  icon: Icon,
  title,
  description,
  checked,
  onCheckedChange,
  children,
}: {
  icon: typeof Wallet;
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  children: React.ReactNode;
}) => (
  <div className={`rounded-2xl border transition-colors ${checked ? 'border-[#c4ab83]/60 bg-white' : 'border-slate-200 bg-slate-50/60'}`}>
    <label className="flex cursor-pointer items-center gap-3 p-4">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${checked ? 'bg-[#c4ab83]/15 text-[#9a7f52]' : 'bg-white text-slate-400'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900">{title}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </label>
    {checked && <div className="space-y-4 border-t border-slate-100 p-4 pt-4 animate-in fade-in slide-in-from-top-1 duration-200">{children}</div>}
  </div>
);

export const SimpleFeeConfig = ({
  caseValue,
  contractConfiguration,
  onContractConfigurationChange,
  isProBono,
  onProBonoChange,
}: SimpleFeeConfigProps) => {
  const [state, setState] = useState<FeeState>(() => stateFromConfig(contractConfiguration, caseValue));

  // Configuração antiga que este formulário não representa (valor fixo, parcelas, eventos)
  const legacyMode =
    contractConfiguration &&
    ((contractConfiguration.mode === 'global_fixed' && (contractConfiguration as any).globalValue > 0) ||
      contractConfiguration.mode === 'specific_installments' ||
      contractConfiguration.mode === 'conditional_events');

  const update = (patch: Partial<FeeState>) => {
    const next = { ...state, ...patch };
    setState(next);
    onContractConfigurationChange(next.hasInitial || next.hasSuccess ? configFromState(next) : null);
  };

  const installmentValue = state.installments > 0 ? state.initialAmount / state.installments : 0;
  const successEstimate =
    state.successType === 'salaries'
      ? state.salaryCount * state.salaryValue
      : state.successType === 'benefit'
        ? (state.percentage / 100) * state.salaryCount * state.salaryValue
        : (state.percentage / 100) * state.baseValue;
  const benefitInstallment = (state.percentage / 100) * state.salaryValue;

  const summary: string[] = [];
  if (state.hasInitial && state.initialAmount > 0) {
    summary.push(
      state.installments > 1
        ? `Iniciais de ${brl(state.initialAmount)} em ${state.installments}x de ${brl(installmentValue)}`
        : `Iniciais de ${brl(state.initialAmount)} à vista`
    );
  }
  if (state.hasSuccess) {
    summary.push(
      state.successType === 'benefit'
        ? `Êxito de ${state.percentage}% de ${state.salaryCount} parcela${state.salaryCount === 1 ? '' : 's'} do benefício${benefitInstallment ? ` (${state.salaryCount}x de ${brl(benefitInstallment)})` : ''}`
        : state.successType === 'salaries'
        ? `Êxito de ${state.salaryCount} salário${state.salaryCount === 1 ? '' : 's'}${successEstimate ? ` (${brl(successEstimate)})` : ''}`
        : `Êxito de ${state.percentage}%${successEstimate ? ` (estimativa ${brl(successEstimate)})` : ''}`
    );
  }

  return (
    <div className="space-y-4">
      {/* Cobrar ou pro bono */}
      <div className="inline-flex rounded-xl bg-slate-100 p-1">
        {[
          { value: false, label: 'Cobrar honorários', icon: Wallet },
          { value: true, label: 'Pro bono', icon: Heart },
        ].map(({ value, label, icon: Icon }) => (
          <button
            key={label}
            type="button"
            onClick={() => onProBonoChange(value)}
            className={`inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-all ${
              isProBono === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Icon className={`h-4 w-4 ${isProBono === value ? (value ? 'text-pink-500' : 'text-[#9a7f52]') : ''}`} />
            {label}
          </button>
        ))}
      </div>

      {isProBono ? (
        <div className="flex items-start gap-3 rounded-2xl border border-pink-100 bg-pink-50/60 p-4 text-sm text-pink-900">
          <Heart className="mt-0.5 h-4 w-4 shrink-0 text-pink-500" />
          <p>Este processo é <strong>pro bono</strong>: nenhum honorário será lançado no Financeiro.</p>
        </div>
      ) : (
        <>
          {legacyMode && (
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Este processo usa um modelo de cobrança antigo, que continua valendo. Ele só será substituído se você
                alterar algo abaixo.
              </p>
            </div>
          )}

          <p className="text-sm text-slate-500">Ative um ou os dois tipos de honorário, conforme o contrato com o cliente.</p>

          {/* Honorários iniciais */}
          <Section
            icon={Wallet}
            title="Honorários iniciais"
            description="Valor combinado na contratação, à vista ou parcelado"
            checked={state.hasInitial}
            onCheckedChange={(v) => update({ hasInitial: v })}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="initial_amount">Valor total</Label>
                <CurrencyInput id="initial_amount" value={state.initialAmount} onChange={(v) => update({ initialAmount: v })} />
              </div>
              <div>
                <Label htmlFor="initial_installments">Parcelas</Label>
                <Select value={String(state.installments)} onValueChange={(v) => update({ installments: Number(v) })}>
                  <SelectTrigger id="initial_installments">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n === 1 ? 'À vista' : `${n}x${state.initialAmount ? ` de ${brl(state.initialAmount / n)}` : ''}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="initial_date">{state.installments > 1 ? 'Vencimento da 1ª parcela' : 'Data do pagamento'}</Label>
                <Input id="initial_date" type="date" value={state.firstDate} onChange={(e) => update({ firstDate: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="initial_method">Forma de pagamento</Label>
                <Select value={state.paymentMethod} onValueChange={(v) => update({ paymentMethod: v as PaymentMethod })}>
                  <SelectTrigger id="initial_method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={state.firstPaid} onCheckedChange={(v) => update({ firstPaid: v === true })} />
              {state.installments > 1 ? 'A 1ª parcela já foi paga' : 'Já foi pago'}
            </label>
          </Section>

          {/* Honorários de êxito */}
          <Section
            icon={Trophy}
            title="Honorários de êxito"
            description="Cobrados só se o processo der certo"
            checked={state.hasSuccess}
            onCheckedChange={(v) => update({ hasSuccess: v })}
          >
            <div>
              <Label>Como é calculado?</Label>
              <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                  { value: 'percentage' as const, title: 'Porcentagem', hint: 'Ex.: 30% do valor da causa' },
                  { value: 'salaries' as const, title: 'Salários', hint: 'Ex.: 2 salários do cargo pretendido' },
                  { value: 'benefit' as const, title: '% do benefício', hint: 'Ex.: 30% de cada um dos 4 salários-maternidade' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => update({ successType: opt.value })}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      state.successType === opt.value
                        ? 'border-[#c4ab83] bg-[#faf7f2]'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className={`text-sm font-semibold ${state.successType === opt.value ? 'text-[#7a6440]' : 'text-slate-800'}`}>{opt.title}</p>
                    <p className="text-xs text-slate-500">{opt.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {state.successType === 'percentage' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="success_pct">Percentual</Label>
                  <div className="relative">
                    <Input
                      id="success_pct"
                      type="number"
                      min={0}
                      max={100}
                      step="0.5"
                      className="pr-8"
                      value={state.percentage || ''}
                      onChange={(e) => update({ percentage: Number(e.target.value) || 0 })}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="success_base">Sobre o valor de</Label>
                  <CurrencyInput id="success_base" value={state.baseValue} onChange={(v) => update({ baseValue: v })} />
                  <p className="mt-1 text-xs text-slate-400">Valor da causa ou da condenação esperada</p>
                </div>
              </div>
            ) : state.successType === 'benefit' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="benefit_pct">Percentual de cada parcela</Label>
                  <div className="relative">
                    <Input
                      id="benefit_pct"
                      type="number"
                      min={0}
                      max={100}
                      step="0.5"
                      className="pr-8"
                      value={state.percentage || ''}
                      onChange={(e) => update({ percentage: Number(e.target.value) || 0 })}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="benefit_count">Parcelas do benefício</Label>
                  <Input
                    id="benefit_count"
                    type="number"
                    min={1}
                    step="1"
                    value={state.salaryCount || ''}
                    onChange={(e) => update({ salaryCount: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                  />
                  <p className="mt-1 text-xs text-slate-400">Ex.: 4 no salário-maternidade</p>
                </div>
                <div>
                  <Label htmlFor="benefit_value">Valor de cada parcela do benefício</Label>
                  <CurrencyInput id="benefit_value" value={state.salaryValue} onChange={(v) => update({ salaryValue: v })} />
                </div>
                <div>
                  <Label htmlFor="benefit_first">Previsão do 1º recebimento</Label>
                  <Input id="benefit_first" type="date" value={state.benefitFirstDate} onChange={(e) => update({ benefitFirstDate: e.target.value })} />
                  <p className="mt-1 text-xs text-slate-400">Opcional. As demais seguem mês a mês.</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="salary_count">Quantidade de salários</Label>
                  <Input
                    id="salary_count"
                    type="number"
                    min={0}
                    step="0.5"
                    value={state.salaryCount || ''}
                    onChange={(e) => update({ salaryCount: Number(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="salary_value">Salário pretendido</Label>
                  <CurrencyInput id="salary_value" value={state.salaryValue} onChange={(v) => update({ salaryValue: v })} />
                </div>
              </div>
            )}

            <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm">
              <span className="text-slate-500">Valor estimado do êxito: </span>
              <strong className="text-slate-900">{brl(successEstimate)}</strong>
              {state.successType === 'benefit' && state.salaryCount > 0 && (
                <span className="text-slate-500"> · {state.salaryCount} parcela{state.salaryCount === 1 ? '' : 's'} de <strong className="text-slate-900">{brl(benefitInstallment)}</strong></span>
              )}
              <p className="mt-0.5 text-xs text-slate-400">
                {state.successType === 'benefit'
                  ? 'Cada parcela vira um lançamento pendente no Financeiro, uma por mês.'
                  : 'Fica pendente no Financeiro até o resultado do processo.'}
              </p>
            </div>
          </Section>

          {/* Resumo */}
          {summary.length > 0 && (
            <div className="rounded-2xl border border-[#c4ab83]/40 bg-[#faf7f2] px-4 py-3 text-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">Resumo do contrato</p>
              <p className="mt-1 text-slate-800">{summary.join(' + ')}</p>
              <p className="mt-1 text-xs text-slate-500">Os lançamentos são criados no Financeiro ao salvar o processo.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
